import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Typography, IconButton, Popover } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import BaseBox from '../_Common/BaseBox';
import NoData from '../_Common/NoData';
import CustomCircularProgress from '../_Common/CustomCircularProgress';
import { useStyles } from './DailyCalendarStyles';
import { ApplicationState } from '../../../../../store';
import { DailyCalendarDay } from '../../../../../store/modules/boxes/types';
import { formatPrice, setPriceColor } from '../../../../../utils/functions';

const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'];
const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

function toDateStr(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function DailyCalendar() {
  const classes = useStyles();
  const { loading, boxes } = useSelector(
    (state: ApplicationState) => state.boxes
  );

  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedDay, setSelectedDay] = useState<DailyCalendarDay | null>(null);

  const data: DailyCalendarDay[] = boxes.DailyCalendar || [];

  const dataMap: Record<string, DailyCalendarDay> = {};
  data.forEach(d => { dataMap[d.date] = d; });

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const todayStr = toDateStr(now.getFullYear(), now.getMonth(), now.getDate());

  function prevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  }

  function nextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  }

  function handleDayClick(e: React.MouseEvent<HTMLDivElement>, day: DailyCalendarDay) {
    setAnchorEl(e.currentTarget);
    setSelectedDay(day);
  }

  function handlePopoverClose() {
    setAnchorEl(null);
    setSelectedDay(null);
  }

  const hasData = data.length > 0;
  const isFirstLoad = loading && !hasData;

  // Monthly summary
  const monthDays = data.filter(d => {
    const parts = d.date.split('-');
    return parseInt(parts[0]) === viewYear && parseInt(parts[1]) === viewMonth + 1;
  });
  const monthTotal = monthDays.reduce((sum, d) => sum + d.total, 0);
  const monthTrades = monthDays.reduce((sum, d) => sum + d.tradeCount, 0);
  const monthWinDays = monthDays.filter(d => d.total > 0).length;
  const monthLossDays = monthDays.filter(d => d.total < 0).length;

  const cells: React.ReactNode[] = [];

  // Empty cells for padding
  for (let i = 0; i < firstDay; i++) {
    cells.push(<div key={'e' + i} className={`${classes.dayCell} ${classes.empty}`} />);
  }

  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = toDateStr(viewYear, viewMonth, d);
    const dayData = dataMap[dateStr];
    let colorClass = classes.neutral;
    if (dayData) {
      colorClass = dayData.total > 0 ? classes.positive : dayData.total < 0 ? classes.negative : classes.neutral;
    }
    const isToday = dateStr === todayStr;

    cells.push(
      <div
        key={d}
        className={`${classes.dayCell} ${colorClass} ${isToday ? classes.today : ''}`}
        onClick={dayData ? (e) => handleDayClick(e, dayData) : undefined}
        style={!dayData ? { cursor: 'default' } : undefined}
      >
        {d}
      </div>
    );
  }

  return (
    <BaseBox title="Calendário">
      {!isFirstLoad && hasData && (
        <div className={classes.container}>
          <div className={classes.navRow}>
            <IconButton size="small" onClick={prevMonth}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography className={classes.monthLabel}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </Typography>
            <IconButton size="small" onClick={nextMonth}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.weekRow}>
            {WEEKDAYS.map(w => (
              <div key={w} className={classes.weekDay}>{w}</div>
            ))}
          </div>
          <div className={classes.grid}>
            {cells}
          </div>

          <div className={classes.summary}>
              <div className={classes.summaryItem}>
                <Typography className={classes.summaryValue} style={{ color: setPriceColor(monthTotal) }}>
                  {formatPrice(monthTotal)}
                </Typography>
                <Typography className={classes.summaryLabel}>Resultado</Typography>
              </div>
              <div className={classes.summaryItem}>
                <Typography className={classes.summaryValue} style={{ color: '#ffffffdd' }}>
                  {monthTrades}
                </Typography>
                <Typography className={classes.summaryLabel}>Trades</Typography>
              </div>
              <div className={classes.summaryItem}>
                <Typography className={classes.summaryValue} style={{ color: '#4BDC4B' }}>
                  {monthWinDays}
                </Typography>
                <Typography className={classes.summaryLabel}>Dias +</Typography>
              </div>
              <div className={classes.summaryItem}>
                <Typography className={classes.summaryValue} style={{ color: '#FF6347' }}>
                  {monthLossDays}
                </Typography>
                <Typography className={classes.summaryLabel}>Dias -</Typography>
              </div>
            </div>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ className: classes.popover }}
          >
            {selectedDay && (
              <div>
                <Typography variant="caption" style={{ color: '#ffffffcc', fontSize: '12px' }}>
                  {selectedDay.date.split('-').reverse().join('/')} — {selectedDay.tradeCount} trade{selectedDay.tradeCount !== 1 ? 's' : ''}
                </Typography>
                <Typography style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: setPriceColor(selectedDay.total),
                  margin: '4px 0 8px',
                }}>
                  {formatPrice(selectedDay.total)}
                </Typography>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#4BDC4B' }}>
                      {formatPrice(selectedDay.profit)}
                    </Typography>
                    <Typography style={{ fontSize: '11px', color: '#ffffffaa' }}>Lucro</Typography>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#FF6347' }}>
                      {formatPrice(selectedDay.loss)}
                    </Typography>
                    <Typography style={{ fontSize: '11px', color: '#ffffffaa' }}>Perda</Typography>
                  </div>
                </div>
              </div>
            )}
          </Popover>
        </div>
      )}
      {isFirstLoad && <CustomCircularProgress />}
      {!isFirstLoad && !hasData && <NoData />}
    </BaseBox>
  );
}
